import { defineStore } from 'pinia';
import { ICatalog, ICatalogAllLike } from '@/storePinia/stores/catalog/interfaces/ICatalog';
import axios from 'axios';
import {
  ICatalogSmartFilter,
  ICatalogSmartFilterItemCheckbox,
  ICatalogSmartFilterItemPrice,
} from '@/storePinia/stores/catalog/interfaces/ICatalogSmartFilter';
import useLoader from '@use/useLoader/useLoader';
import router from '@/routs/router';
import { useResultsStore } from '@/storePinia/stores/results';
import { IBreadcrumbs } from '@/interfaces';
import { ISiteLanguages } from '@/storePinia/stores/results/ISiteLanguages';
import { usePersonalStore } from '@/storePinia/stores/personal';

const loader = useLoader();

/** sub price-range value (елемент з priceRange.VALUES) + мітка */
type ISubPriceRangeValue = ICatalogSmartFilterItemPrice['priceRange']['VALUES'][number] & {
  isSubPriceRange: true;
};

/** checkbox value (елемент з VALUES чекбокса) + CODE фільтра */
type ICheckboxSelectedValue = ICatalogSmartFilterItemCheckbox['VALUES'][string] & { CODE: string };

/** price range item з доданим printRange */
type IPriceSelectedItem = ICatalogSmartFilterItemPrice & { printRange: string };

/** те, що реально повертає getter getSelectedFilters */
type ISelectedFilter = ICheckboxSelectedValue | IPriceSelectedItem | ISubPriceRangeValue;

type ICatalogGetters = {
  getSelectedFilters: (state: ICatalog) => ISelectedFilter[];
  clearUrlParam: (state: ICatalog) => (payload: { url: string; param: string }) => string;
  getCatalogSmartFilterITEMS: (state: ICatalog) => ICatalogSmartFilter['ITEMS'];
};

type IChangePageDataProps = {
  url: string;
};

type ICatalogActions = {
  changeFilter: () => Promise<void>;
  changePageData: (payload: string | IChangePageDataProps) => Promise<void>;
  getMoreItems: () => Promise<ICatalog | undefined>;
  addUserCatalogFilter: (payload: { filterName: string }) => Promise<
    | {
        response: {
          status: boolean;
          text: string;
        };
      }
    | undefined
  >;
  deleteSelectedItem: (payload: {
    CODE: string;
    CONTROL_ID: string;
    priceRange?: boolean;
    isSubPriceRange: boolean;
  }) => void;
  updatePageData: () => Promise<void>;
};

const isPriceItem = (
  item: ICatalogSmartFilterItemCheckbox | ICatalogSmartFilterItemPrice
): item is ICatalogSmartFilterItemPrice => 'priceRange' in item;

export const useCatalogStore = defineStore<'catalog', ICatalog, ICatalogGetters, ICatalogActions>(
  'catalog',
  {
    state: (): ICatalog => ({
      allLikes: {},
      catalogSectionList: {},
      catalogSmartFilter: {},
      catalogSort: {},
      collection: {},
      isOpenFilter: false,
      productListCardType: 2,
      vuexHoliday: {},
    }),
    getters: {
      getSelectedFilters: (state) => {
        const items = state.catalogSmartFilter?.ITEMS;
        const arr: ISelectedFilter[] = [];

        if (!items) return arr;

        items.forEach((item) => {
          // PRICE (DISPLAY_TYPE === 'A') — але обов’язково ще й type-guard
          if (item.DISPLAY_TYPE === 'A' && isPriceItem(item)) {
            const range: Record<string, string> = {};

            // тут TS вже знає, що item = ICatalogSmartFilterItemPrice
            const values = item.VALUES;

            if (values.MIN?.HTML_VALUE) range.MIN = values.MIN.HTML_VALUE;
            if (values.MAX?.HTML_VALUE) range.MAX = values.MAX.HTML_VALUE;

            let printRange = '';
            if (range.MIN && range.MAX) printRange = `от ${range.MIN} до ${range.MAX}`;
            else if (range.MIN) printRange = `от ${range.MIN}`;
            else if (range.MAX) printRange = `до ${range.MAX}`;

            const newObj: IPriceSelectedItem = {
              ...item,
              printRange,
            };

            if (newObj.printRange) {
              arr.push(newObj);
            }

            if (Array.isArray(item.priceRange?.VALUES) && item.priceRange.VALUES.length > 0) {
              item.priceRange.VALUES.forEach((value) => {
                if (value.CHECKED) {
                  arr.push({
                    ...value,
                    isSubPriceRange: true,
                  });
                }
              });
            }

            return;
          }

          // CHECKBOX
          const subObj = (item as ICatalogSmartFilterItemCheckbox).VALUES;
          if (subObj) {
            Object.values(subObj).forEach((value) => {
              if (value.CHECKED) {
                arr.push({
                  ...value,
                  CODE: item.CODE,
                });
              }
            });
          }
        });

        return arr;
      },

      clearUrlParam: () => (payload) => {
        const url = payload.url.split('?');
        if (url?.[1]) {
          return url
            .map((el, ind) => {
              switch (ind) {
                case 1: {
                  const searchParams = new URLSearchParams(`?${el}`);
                  searchParams.delete(payload.param);
                  return searchParams.toString();
                }
                default:
                  return el;
              }
            })
            .join('?');
        }
        return payload.url;
      },
      getCatalogSmartFilterITEMS: (state) => {
        if (!state.catalogSmartFilter.ITEMS) return [];

        return state.catalogSmartFilter.ITEMS.filter((item) => item.CODE !== 'BASE');
      },
    },
    actions: {
      deleteSelectedItem(payload) {
        try {
          const ITEMS: ICatalog['catalogSmartFilter']['ITEMS'] = JSON.parse(
            JSON.stringify(this.catalogSmartFilter.ITEMS)
          );

          const newItems = ITEMS?.map((el) => {
            if (el.CODE === payload.CODE) {
              if (el.DISPLAY_TYPE !== 'A') {
                /** изменяем состояние обычного checkbox * */

                Object.values(el.VALUES).forEach((item) => {
                  if (item.CONTROL_ID === payload.CONTROL_ID) {
                    // eslint-disable-next-line no-param-reassign
                    item.CHECKED = false;
                  }
                });
              } else {
                /** изменяем состояние range price * */
                const min = el.VALUES?.MIN;
                if (min && Object.prototype.hasOwnProperty.call(min, 'HTML_VALUE')) {
                  delete (min as { HTML_VALUE?: string }).HTML_VALUE;
                }

                const max = el.VALUES?.MAX;
                if (max && Object.prototype.hasOwnProperty.call(max, 'HTML_VALUE')) {
                  delete (max as { HTML_VALUE?: string }).HTML_VALUE;
                }
              }
            } else if (payload.priceRange) {
              // remove price range
              (el as ICatalogSmartFilterItemPrice)?.priceRange?.VALUES.forEach((item) => {
                if (item.CONTROL_ID === payload.CONTROL_ID) {
                  // eslint-disable-next-line no-param-reassign
                  item.CHECKED = false;
                }
              });
            }
            // delete sub price range
            else if (payload.isSubPriceRange && el.CODE === 'MINIMUM_PRICE') {
              return {
                ...el,
                priceRange: {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  ...el.priceRange,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  VALUES: el.priceRange.VALUES.map((item) => {
                    if (item.CONTROL_ID === payload.CONTROL_ID) {
                      return { ...item, CHECKED: false };
                    }
                    return item;
                  }),
                },
              };
            }

            return el;
          });

          this.$patch({ catalogSmartFilter: { ...this.catalogSmartFilter, ITEMS: newItems } });
        } catch (err) {
          console.error(err);
        }
      },
      /**
       * change catalog filter
       * * */
      async changeFilter() {
        try {
          loader.start();
          const form: HTMLFormElement | null = window.document.querySelector('.filter');
          if (form) {
            const formData = new FormData(form);

            /** create query string from filter form * */
            const formEntries = [...formData.entries()];
            const asString = formEntries
              .map(
                ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
              )
              .join('&');

            if (!formData) throw new Error('form is undefined');

            /** бек формирует url на основании отправленной формы * */
            const response = await axios.get(`${window.location.pathname}?${asString}`);
            const { SEF_SET_FILTER_URL, FILTER_URL } = response.data;
            const url = SEF_SET_FILTER_URL || FILTER_URL;

            /** делаем запрос по сформированном url и и обновляем стору * */
            if (url) {
              await this.changePageData(url);
            } else {
              throw new Error(`${response} url не сформовано`);
            }
          }
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },

      async changePageData(payload) {
        const resultsStore = useResultsStore();
        let url: string;

        if (typeof payload === 'string') {
          url = payload;
        } else {
          url = payload.url;
        }

        /** using crutches, be careful */
        if (url) {
          url = this.clearUrlParam({ url, param: 'api' });
        }

        try {
          loader.start();

          interface IResponse {
            siteLanguages?: ISiteLanguages;
            breadcrumbs?: IBreadcrumbs;
            allLikes?: ICatalogAllLike;
          }

          const response: { status: number; data: IResponse } = await axios.get(url, {
            params: { api: 'y' },
          });

          const { siteLanguages, breadcrumbs, allLikes } = response.data;

          if (siteLanguages) {
            resultsStore.$patch({ siteLanguages });
            delete response.data.siteLanguages;
          }

          if (breadcrumbs) {
            resultsStore.$patch({ breadcrumbs });
            delete response.data.breadcrumbs;
          }
          if (allLikes) {
            resultsStore.$patch({
              allLikes: {
                ...resultsStore.allLikes,
                ...allLikes,
              },
            });
          }

          if (response.status === 200) {
            // commit('catalog/setState', response.data);

            this.$patch(response.data);

            router.push(url);
          }
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },

      async getMoreItems() {
        try {
          if (this.catalogSectionList.pagination) {
            loader.start();
            const { pagingKey, pageNumber } = this.catalogSectionList.pagination;

            if (pagingKey && pageNumber) {
              const config = {
                params: {
                  api: 'y',
                  [pagingKey]: +pageNumber + 1,
                },
              };

              const {
                status,
                data: response,
              }: {
                status: number;
                data: ICatalog;
              } = await axios.get(window.location.href, config);

              // const copyResponse = JSON.parse(JSON.stringify(response));

              if (status === 200) {
                this.$state = {
                  ...this.$state,
                  catalogSectionList: {
                    ...response.catalogSectionList,
                    items: [
                      ...(this.catalogSectionList.items ?? []),
                      ...(response?.catalogSectionList?.items ?? []),
                    ],
                  },
                };
                // this.$patch({
                //   ...copyResponse,
                //   catalogSectionList: {
                //     ...response.catalogSectionList,
                //     items: [
                //       ...(this.catalogSectionList.items ?? []),
                //       ...(response?.catalogSectionList?.items ?? []),
                //     ],
                //   },
                // });

                if (response?.allLikes) {
                  const resultsStore = useResultsStore();
                  resultsStore.$patch({
                    allLikes: {
                      ...resultsStore.allLikes,
                      ...response?.allLikes,
                    },
                  });
                }
              }
              return response;
            }
          }
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },

      async addUserCatalogFilter(payload) {
        try {
          loader.start();
          if (this.catalogSmartFilter.routes?.addUserCatalogFilter) {
            const personalStore = usePersonalStore();
            const {
              path,
              'params-example-data': { action },
            } = this.catalogSmartFilter.routes.addUserCatalogFilter;

            const userId = personalStore.authInfo?.user_id;
            const savedFilters = this.catalogSmartFilter?.savedFilters;

            const requestData = {
              action,
              userId,
              catalogUrl: savedFilters?.catalogUrl,
              filterUrl: savedFilters?.catalogFilterUrl,
              filterValue: savedFilters?.filterValue,
              ...payload,
            };

            const {
              data,
            }: {
              data: {
                response: {
                  status: boolean;
                  text: string;
                };
              };
            } = await axios.post(path, requestData);

            if (data.response.status === true && typeof savedFilters?.filterUrl === 'string') {
              await this.changePageData(savedFilters?.filterUrl);
            }

            return data;
          }
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },

      async updatePageData() {
        try {
          loader.start();

          const response: {
            status: number;
            data: ICatalog;
          } = await axios.get(window.location.href, { params: { api: 'y' } });
          if (response.status === 200 && response.data) {
            this.$state = {
              ...this.$state,
              ...response.data,
            };
          }
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
    },
  }
);
